/**
 * FX advisory — WHAT CONVERTS, ANSWERED BY THE SERVER (ZEN-108).
 *
 * WHAT THIS FILE USED TO BE. A hard-coded list of the ~31 European Central Bank
 * reference currencies, and a helper that answered "can this convert?" from it.
 * That was a SECOND COPY of an answer the server already owns, and the two
 * drifted: the server converts roughly 153 currencies, because `get_fx_rate`
 * falls back on its own to the United States Treasury reporting rate whenever
 * the target is US dollars. The Settings currency table, reading the short copy,
 * warned 121 pickable currencies as needing a hand-typed exchange rate three
 * lines above a paragraph saying rates are only ever asked for when NEITHER
 * source publishes one.
 *
 * THE LIST IS GONE ON PURPOSE. Correcting it here would have re-created the same
 * defect one map larger. `/api/fx/coverage` serves `fx.usd_conversion_coverage()`,
 * which is derived from the very constants the converter consults, so a currency
 * added to the server updates every screen with nothing to remember here.
 */
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { curCode } from "@/lib/currencies";

/**
 * The server's coverage answer, fetched once and cached for the session.
 *
 * Coverage describes what this deployment's converter can do. It is identical
 * for every family and changes only when the app is redeployed, so it is held
 * indefinitely rather than re-fetched per screen.
 */
export function useFxCoverage() {
  const q = useQuery({
    queryKey: ["fx-coverage"],
    queryFn: async () => (await api.get("/fx/coverage")).data,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: 1,
  });

  // WHY THIS IS NOT `isError`. A screen that says "Checking..." forever is the
  // failure this returned instead when the endpoint was taken away in testing:
  // the query never reached its error state, so the explanation never appeared
  // and the badges sat on "Checking..." indefinitely. `unavailable` therefore
  // asks the question the screen actually needs answered - is there an answer,
  // and is one on the way - which covers a failed query, a paused one, and one
  // that settled with nothing, without depending on which of those it was.
  const arriving = q.isPending && q.fetchStatus === "fetching";
  return { coverage: q.data, unavailable: !q.data && !arriving };
}

/**
 * How a single currency converts, given the server's answer.
 *
 * Returns one of:
 *   { state: "base" }                          — US dollars; nothing to convert
 *   { state: "auto", source: "ecb"|"treasury", toUsd, fromUsd }
 *   { state: "manual" }                        — no source publishes it
 *   { state: "unknown" }                       — the answer has not arrived yet
 *
 * ABSENT IS NOT UNKNOWN. Once the answer HAS arrived, a currency missing from it
 * means the one thing it can mean: neither source publishes a rate, so somebody
 * has to type one. Before it arrives we say "unknown" rather than guessing, so a
 * screen never renders a confident claim it cannot support yet.
 */
export function fxCoverageFor(coverage, currency) {
  const cur = curCode(currency, "USD");
  if (!coverage || !coverage.currencies) return { state: "unknown" };
  const entry = coverage.currencies[cur];
  if (!entry || !entry.auto) return { state: "manual" };
  if (entry.source === "base") return { state: "base" };
  return {
    state: "auto",
    source: entry.source,
    toUsd: entry.to_usd !== false,
    fromUsd: entry.from_usd === true,
  };
}

/** Plain-English name of a rate source, for anything a person reads. */
export function fxSourceLabel(source) {
  if (source === "ecb") return "European Central Bank";
  if (source === "treasury") return "US Treasury";
  return "";
}

// A compatibility shim named `can_autoconvert_ecb` used to live here, kept only
// because Gifts.jsx belonged to work then in flight (ZEN-351) and renaming
// across another lane's file would collide. ZEN-351 shipped; the shim and its
// module-level snapshot are retired (ZEN-388). The two former callers now use
// `useFxCoverage` + `fxCoverageFor` like everything else.
